import React from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { SalesActivityTypeCount } from "./activitiesReportService";
import {
  CATEGORICAL_PALETTE,
  renderTooltipCard,
  renderTooltipHeader,
  renderTooltipStatusRow,
} from "./activityChartPalette";

interface ActivityStatusChartProps {
  types: SalesActivityTypeCount[];
  selectedTypeId: number | null;
  onSelectType: (typeId: number) => void;
  onClearType: () => void;
  selectedStatusId: number | null;
  onSelectStatus: (statusId: number) => void;
}

// ApexCharts keeps running its own internal click-handling code (hover state,
// tooltip teardown, etc.) after dataPointSelection fires. If that callback
// triggers a React state update that unmounts/remounts this same chart (the
// `key` below does that on purpose to force a clean redraw), it can race
// ApexCharts' own cleanup and throw "Cannot read properties of null
// (reading 'querySelector')". Deferring the state update to the next tick
// lets ApexCharts finish first.
const deferSelection = (fn: () => void) => setTimeout(fn, 0);

const ActivityStatusChart: React.FC<ActivityStatusChartProps> = ({
  types,
  selectedTypeId,
  onSelectType,
  onClearType,
  selectedStatusId,
  onSelectStatus,
}) => {
  const selectedType =
    types.find((t) => t.crm_sales_activity_type_id === selectedTypeId) || null;

  if (types.length === 0) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ height: "320px" }}
      >
        <div className="text-center">
          <i className="ti ti-status-change fs-1 text-muted mb-3" />
          <p className="text-muted mb-0">No activity data available yet</p>
        </div>
      </div>
    );
  }

  const sharedOptions: Partial<ApexOptions> = {
    chart: {
      type: "donut",
      height: 320,
      // This chart remounts on every drill-down (its `key` below changes
      // with the selected type), and that can race a sibling chart's own
      // in-flight update in the same cascade — disabling animations
      // removes the async timers that would otherwise touch a destroyed
      // instance's internals.
      animations: { enabled: false },
      dropShadow: {
        enabled: true,
        top: 2,
        left: 0,
        blur: 8,
        opacity: 0.18,
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "diagonal1",
        shadeIntensity: 0.3,
        opacityFrom: 1,
        opacityTo: 0.85,
      },
    },
    dataLabels: {
      enabled: true,
      style: { fontWeight: 700 },
      formatter: (val: number) => `${val.toFixed(0)}%`,
    },
    legend: { position: "bottom", fontWeight: 600, markers: { size: 7 } },
    stroke: { width: 3, colors: ["#fcfcfb"] },
  };

  if (!selectedType) {
    // Default view: every activity type as a slice. Hovering a slice also
    // reveals that type's status breakdown; clicking one drills into it.
    const labels = types.map((t) => t.crm_sales_activity_type_display_name);
    const series = types.map((t) => t.counts);
    const grandTotal = series.reduce((sum, val) => sum + val, 0);
    const colors = types.map(
      (_, index) => CATEGORICAL_PALETTE[index % CATEGORICAL_PALETTE.length]
    );

    const options: ApexOptions = {
      ...sharedOptions,
      chart: {
        ...sharedOptions.chart,
        events: {
          dataPointSelection: (_event, _ctx, config) => {
            const type = types[config.dataPointIndex];
            if (type) deferSelection(() => onSelectType(type.crm_sales_activity_type_id));
          },
        },
      },
      labels,
      colors,
      tooltip: {
        theme: "light",
        custom: ({ seriesIndex, w }) => {
          const type = types[seriesIndex];
          const statusRows = (type.statuses || [])
            .map((s) =>
              renderTooltipStatusRow(
                s.crm_sales_activity_type_status_display_name,
                s.counts
              )
            )
            .join("");
          const swatch = w.globals.colors[seriesIndex];
          return renderTooltipCard(
            renderTooltipHeader(
              type.crm_sales_activity_type_display_name,
              type.counts,
              swatch
            ) + statusRows
          );
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "66%",
            labels: {
              show: true,
              value: { fontSize: "26px", fontWeight: 700 },
              total: {
                show: true,
                label: "Total",
                fontSize: "13px",
                formatter: () => String(grandTotal),
              },
            },
          },
        },
      },
    };

    return (
      <>
        <Chart key="all-types" options={options} series={series} type="donut" height={320} />
        <p className="text-muted fs-12 mb-0 mt-2">
          <i className="ti ti-info-circle me-1" />
          Hover a slice for its status breakdown, or click to drill in.
        </p>
      </>
    );
  }

  // Drilled-in view: status breakdown for the selected activity type.
  const statuses = [...(selectedType.statuses || [])].sort(
    (a, b) => a.crm_sales_activity_type_status_id - b.crm_sales_activity_type_status_id
  );

  if (statuses.length === 0 || statuses.every((s) => s.counts === 0)) {
    return (
      <>
        <button
          type="button"
          className="btn btn-link btn-sm p-0 mb-2 text-decoration-none"
          onClick={onClearType}
        >
          <i className="ti ti-arrow-left me-1" />
          All activity types
        </button>
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ height: "280px" }}
        >
          <div className="text-center">
            <i className="ti ti-status-change fs-1 text-muted mb-3" />
            <p className="text-muted mb-0">
              No status records found for {selectedType.crm_sales_activity_type_display_name}
            </p>
          </div>
        </div>
      </>
    );
  }

  const labels = statuses.map((s) => s.crm_sales_activity_type_status_display_name);
  const series = statuses.map((s) => s.counts);
  const colors = statuses.map(
    (_, index) => CATEGORICAL_PALETTE[index % CATEGORICAL_PALETTE.length]
  );

  const options: ApexOptions = {
    ...sharedOptions,
    chart: {
      ...sharedOptions.chart,
      events: {
        dataPointSelection: (_event, _ctx, config) => {
          const status = statuses[config.dataPointIndex];
          if (status) {
            deferSelection(() =>
              onSelectStatus(status.crm_sales_activity_type_status_id)
            );
          }
        },
      },
    },
    labels,
    colors,
    plotOptions: {
      pie: {
        donut: {
          size: "66%",
          labels: {
            show: true,
            value: { fontSize: "26px", fontWeight: 700 },
            total: {
              show: true,
              label: selectedType.crm_sales_activity_type_display_name,
              fontSize: "13px",
              formatter: () => String(selectedType.counts),
            },
          },
        },
      },
    },
    tooltip: {
      y: { formatter: (val: number) => `${val} activities` },
    },
    states: {
      hover: { filter: { type: "darken" } },
      active: {
        filter: {
          type: selectedStatusId ? "none" : "darken",
        },
      },
    },
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 mb-2 text-decoration-none"
        onClick={onClearType}
      >
        <i className="ti ti-arrow-left me-1" />
        All activity types
      </button>
      <Chart
        key={selectedType.crm_sales_activity_type_id}
        options={options}
        series={series}
        type="donut"
        height={320}
      />
    </>
  );
};

export default ActivityStatusChart;
